import { useMemo } from "react";
import { useFormContext } from "react-hook-form";
import useFilterDetails from "../../../useFilterDetails";
import { useFilterBuilderContext } from "../../FilterBuilderContext";
import FilterValueSelection, {
  type FilterSelectionValue,
} from "./FilterValueSelection";
import { Tooltip } from "@SeemplicityDev/shared";
import type {
  FilterItem,
  FilterPinItem,
} from "../../../../FilterManagement/CommonFilterTypes";
import type { ConditionOption } from "../FilterConditionSelect";

export interface FilterValueSelectProps {
  isEditable?: boolean;
  conditionOptions?: ConditionOption[];
  onChange: (value: FilterSelectionValue) => void;
  currentValue: FilterSelectionValue;
  isActive: boolean;
  onClick: () => void;
  setValid: (isValid: boolean) => void;
  filtersToPin?: FilterPinItem[];
}

const FilterValueSelect: React.FC<FilterValueSelectProps> = ({
  isEditable = true,
  onChange,
  currentValue,
  isActive,
  onClick,
  setValid,
  filtersToPin = [],
}) => {
  const { watch } = useFormContext();
  const {
    filterBuilderState: { fieldsData, statusesData, entity },
  } = useFilterBuilderContext();

  const conditionValue = watch("condition");
  const fieldValue = watch("field");

  const { selectedValueData, fieldData } = useFilterDetails(
    fieldsData,
    fieldValue,
    conditionValue,
    // useFilterDetails accepts FilterValueLike (FilterItem["value"] | undefined);
    // FilterSelectionValue is a broader runtime union (adds null and the
    // {value, unit} date shape). The value is runtime-compatible, so bridge it.
    (currentValue ?? undefined) as FilterItem["value"] | undefined,
    [],
    filtersToPin
  );

  const conditionWithValuesList = useMemo(() => {
    return (
      conditionValue === "in" ||
      conditionValue === "not_in" ||
      conditionValue === "overlap" ||
      conditionValue === "notoverlapping" ||
      !!fieldData?.display_options
    );
  }, [conditionValue, fieldData]);

  return (
    <div className={"filter-value-selector-wrapper"}>
      <Tooltip content={selectedValueData.title}>
        <div
          data-testid={"filter-value-chip"}
          className={`filter-row-selected-value   ${!isEditable ? "is-disabled" : ""} ${isActive ? "is-active" : ""}`}
          onClick={() => {
            if (isEditable) {
              onClick();
            }
          }}
        >
          {selectedValueData.label || "Select value"}
        </div>
      </Tooltip>
      {isActive && (
        <div className={"filter-condition-component-wrapper"}>
          <FilterValueSelection
            entity={entity ?? undefined}
            namePrefix={`${fieldValue}-${conditionValue}-value`}
            value={currentValue}
            statuses={statusesData}
            fieldValue={fieldValue}
            conditionValue={conditionValue}
            onChange={(val) => onChange(val)}
            conditionWithValuesList={conditionWithValuesList}
            fieldsData={fieldsData}
            setValid={setValid}
          />
        </div>
      )}
    </div>
  );
};

export default FilterValueSelect;
